// 261023 E3-P3h4a (FE) — dòng «Chấm → Sửa vòng k/2 → Chấm lại» của chuỗi Truyện mới, dựng từ `workbench.chainProgress` (BE `ChainProgressView`).
// THUẦN: KHÔNG import antd / react. Chữ của từng bước lấy NGUYÊN từ BE (`text`, đã sạch mã nội bộ và tiền) — FE chỉ chọn tone / biểu tượng, không tự chế chữ.
// Luật hiển thị (contract BE §5 + ghi chú Reviewer):
//  - chỉ `outcome:'CLEAN'` (và `complete:true`) mới có ✓ «đạt»; `SKIPPED` / `STALE` / `ERROR` / `UNRESOLVED` không bao giờ ✓.
//  - `complete:false` / bước `UNKNOWN` ⇒ «?» (chưa rõ), không ✓.
//  - thiếu unit của một bước = «chưa có gì để vẽ» (KHÔNG suy «chưa chạy» hay «đã xong»); `chainProgress` thiếu / null / sai phiên bản ⇒ [] (giữ hiển thị cũ).
import type { ChainProgressStep, ChainProgressUnit, ChainProgressView } from '../../../types/adaptationWorkbench';
import { redactMoney } from './moneyRedact';

/** `rejected` (3h4a2): vòng sửa bị BE từ chối — khác lỗi (`warn`), không ✓. */
export type ChipTone = 'done' | 'now' | 'warn' | 'rejected' | 'unknown' | 'skip' | 'idle';
export interface ChainChip { key: string; text: string; tone: ChipTone; icon: '✓' | '…' | '!' | '✕' | '?' | '–' | '•' | '' }
export interface ChainRow { key: string; stage: string; label: string; running: boolean; chips: ChainChip[] }

/** Nhãn hàng theo mockup: «④ Chia tập». Khoá lạ (BE thêm bước mới) không có nhãn ⇒ bị bỏ qua. ⑤ = C2 critic từng tập (3h4a2): mỗi tập một hàng «⑤ Kịch bản · tập n». */
const STAGE_ROW_LABEL: Record<string, string> = { source: '① Nguồn', skeleton: '② Khung xương', localize: '③ Bản địa hoá', split: '④ Chia tập', script: '⑤ Kịch bản' };
/** Các bước máy chấm/sửa theo chặng của dải (`NsStageKey`): Chặng 1 = ① ②, Chặng 2 = ③ ④ ⑤. */
const STAGES_OF_CHANG: Record<string, string[]> = { c1: ['source', 'skeleton'], c2: ['localize', 'split', 'script'], c3: [] };

export const CHAIN_CAPTION = 'Mỗi bước: chấm → sửa (tối đa 2 vòng) → chấm lại. Hết 2 vòng còn lỗi thì máy dừng và hỏi bạn.';

const clean = (t: unknown): string => redactMoney(typeof t === 'string' ? t : '').trim();

/** `tidy`: đơn vị đã KẾT THÚC mà không sạch ⇒ bước Chấm / Chấm lại «chạy xong» chỉ là việc đã làm, KHÔNG phải «đạt» ⇒ dấu trung tính «•», không ✓. */
const stepChip = (s: ChainProgressStep, i: number, tidy: boolean): ChainChip | null => {
  const text = clean(s.text);
  if (!text) return null;
  const key = `h${i}`;
  switch (s.result) {
    case 'COMPLETED': case 'ACCEPTED':
      if (!tidy && (s.phase === 'JUDGE' || s.phase === 'REJUDGE')) return { key, text, tone: 'idle', icon: '•' };
      return { key, text, tone: 'done', icon: '✓' };
    case 'REJECTED': return { key, text, tone: 'rejected', icon: '✕' };   // bản sửa bị từ chối: đúng chữ BE, không ✓
    case 'ERROR': return { key, text, tone: 'warn', icon: '!' };
    case 'SKIPPED': return { key, text, tone: 'skip', icon: '–' };
    default: return { key, text, tone: 'unknown', icon: '?' };   // UNKNOWN / giá trị lạ ⇒ chưa rõ
  }
};

/** Chip cuối = câu trạng thái của đơn vị. ✓ chỉ khi DONE + CLEAN + complete. */
const finalChip = (u: ChainProgressUnit): ChainChip | null => {
  const text = clean(u.text);
  if (!text) return null;
  if (u.state === 'RUNNING') return { key: 'live', text, tone: 'now', icon: '…' };
  if (u.state !== 'DONE') return { key: 'state', text, tone: 'idle', icon: '' };
  switch (u.outcome) {
    case 'CLEAN': return u.complete ? { key: 'final', text, tone: 'done', icon: '✓' } : { key: 'final', text, tone: 'unknown', icon: '?' };
    case 'UNRESOLVED': case 'ERROR': return { key: 'final', text, tone: 'warn', icon: '!' };
    case 'SKIPPED': case 'STALE': return { key: 'final', text, tone: 'skip', icon: '–' };
    default: return { key: 'final', text, tone: u.complete ? 'idle' : 'unknown', icon: u.complete ? '' : '?' };
  }
};

export const chipsOfUnit = (u: ChainProgressUnit): ChainChip[] => {
  const tidy = !(u.state === 'DONE' && u.outcome !== 'CLEAN');   // DONE mà không CLEAN ⇒ không ✓ cho bước chấm
  const hist = (Array.isArray(u.history) ? u.history : []).map((s, i) => stepChip(s, i, tidy)).filter((c): c is ChainChip => !!c);
  const last = finalChip(u);
  return last ? [...hist, last] : hist;
};

const usable = (cp: ChainProgressView | null | undefined): ChainProgressUnit[] => (cp && cp.version === 1 && Array.isArray(cp.units) ? cp.units : []);

const rowOf = (u: ChainProgressUnit): ChainRow | null => {
  const base = STAGE_ROW_LABEL[u.stage];
  if (!base) return null;
  const chips = chipsOfUnit(u);
  if (chips.length === 0) return null;
  const label = typeof u.unitIndex === 'number' && u.unitKind === 'volume' ? `${base} · quyển ${u.unitIndex}`
    : typeof u.unitIndex === 'number' && u.unitKind === 'episode' ? `${base} · tập ${u.unitIndex}` : base;
  return { key: u.key, stage: u.stage, label, running: u.state === 'RUNNING', chips };
};

/** Hàng của các bước đã cho, theo thứ tự BE gửi (① quyển tăng dần → ② → ③ → ④). Bước không có unit ⇒ không hàng. */
export const rowsOfStages = (cp: ChainProgressView | null | undefined, stages: ReadonlyArray<string>): ChainRow[] =>
  usable(cp).filter((u) => stages.includes(u.stage)).map(rowOf).filter((r): r is ChainRow => !!r);

/** Hộp «Máy đang chạy bước này» / ghi chú ①: mỗi unit của đúng bước một hàng. */
export const boxRowsOf = (cp: ChainProgressView | null | undefined, stage: string): ChainRow[] => rowsOfStages(cp, [stage]);

/** Bước có nhiều đơn vị cùng loại (① quyển, ⑤ tập): đơn vị chấm SẠCH gộp một hàng «Đã chấm xong n <loại>» ở dải. */
const COLLAPSE_UNIT: Record<string, string> = { source: 'quyển', script: 'tập' };

/**
 * Dải máy tự chạy: các bước của chặng đang mở. ①: quyển đã chấm SẠCH (DONE + CLEAN + complete) gộp thành MỘT hàng «Đã chấm xong n quyển»
 * (đặt sau các quyển đang chạy / chờ / có vấn đề, vẫn hiện riêng); quyển «chưa rõ» không bao giờ bị gộp vào «xong».
 * KHÔNG có mẫu số: BE chỉ gửi unit đã có biên lai / con trỏ pha, không biết tổng số quyển ⇒ «n/m» sẽ nói «xong hết» khi mới chấm 3/5. Câu trung tính, không ✓.
 */
export const bandRowsOf = (cp: ChainProgressView | null | undefined, chang: string): ChainRow[] => {
  const stages = STAGES_OF_CHANG[chang] ?? [];
  if (stages.length === 0) return [];
  const units = usable(cp).filter((u) => stages.includes(u.stage));
  const isClean = (u: ChainProgressUnit): boolean => u.stage in COLLAPSE_UNIT && u.state === 'DONE' && u.outcome === 'CLEAN' && u.complete === true;
  const out: ChainRow[] = [];
  for (const stage of stages) {
    units.filter((u) => u.stage === stage && !isClean(u)).map(rowOf).forEach((r) => { if (r) out.push(r); });
    const noun = COLLAPSE_UNIT[stage];
    const cleanN = noun ? units.filter((u) => u.stage === stage && isClean(u)).length : 0;
    if (noun && cleanN > 0) {
      // KHÔNG mẫu số, KHÔNG ✓: BE chỉ gửi unit đã có biên lai / con trỏ pha (⑤: tập chưa tới C2 không có unit) ⇒ không biết «hết chưa».
      out.push({ key: `${stage}:done`, stage, label: STAGE_ROW_LABEL[stage], running: false, chips: [{ key: 'sum', text: `Đã chấm xong ${cleanN} ${noun}`, tone: 'idle', icon: '' }] });
    }
  }
  return out;
};

/** Loại dải mà máy ĐANG làm việc / vừa dừng giữa chặng ⇒ có dòng chấm/sửa. Chờ duyệt, Bắt đầu, xong, huỷ: không (chưa/không còn vòng nào đang diễn ra). */
const BAND_KINDS: ReadonlySet<string> = new Set(['running', 'blocked', 'interrupted', 'wait_budget', 'wait_review_gate']);
const ACTIVE_STATES: ReadonlySet<string> = new Set(['run', 'wait', 'stopped', 'error']);

/** Dải: hàng của chặng đang mở + chú thích; không có gì để vẽ ⇒ `{rows: [], caption: null}` (dải giữ nguyên như cũ). */
export const bandChainOf = (
  cp: ChainProgressView | null | undefined,
  bar: { kind: string; stages: ReadonlyArray<{ key: string; state: string }> },
): { rows: ChainRow[]; caption: string | null } => {
  if (!BAND_KINDS.has(bar.kind)) return { rows: [], caption: null };
  const active = bar.stages.find((g) => ACTIVE_STATES.has(g.state));
  const rows = active ? bandRowsOf(cp, active.key) : [];
  return rows.length > 0 ? { rows, caption: CHAIN_CAPTION } : { rows: [], caption: null };
};
